import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, X } from "lucide-react";
import { useAmbience } from "@/context/Ambience";
import { TID } from "@/constants/testIds";

const AmbientDock = () => {
  const { enabled, volume, toggle, setVolume } = useAmbience();
  const [openPanel, setOpenPanel] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.2, duration: 0.6 }}
      className="fixed bottom-6 left-6 z-40 flex flex-col-reverse items-start gap-2"
    >
      <div
        role="group"
        aria-label="Temple ambience"
        className="glass-strong rounded-full flex items-center gap-1 pl-1 pr-1 py-1"
      >
        <button
          type="button"
          data-testid={TID.ambientToggle}
          onClick={toggle}
          onContextMenu={(e) => { e.preventDefault(); setOpenPanel((v) => !v); }}
          aria-label={enabled ? "Mute temple ambience" : "Play temple ambience"}
          className="flex items-center gap-3 rounded-full hover:bg-white/5 py-1.5 pl-2 pr-3 transition"
        >
          <div className="w-9 h-9 rounded-full bg-[#FF9933]/15 border border-[#FF9933]/40 flex items-center justify-center">
            {enabled
              ? <Volume2 size={16} className="text-[#FF9933]" />
              : <VolumeX size={16} className="text-white/80" />}
          </div>
          {enabled ? (
            <div className="flex items-end gap-[3px] h-4">
              {[0.15, 0.4, 0.25, 0.55].map((d, i) => (
                <span key={i} className="w-[3px] bg-[#FF9933] eq-bar rounded-sm" style={{ height: "100%", animationDelay: `${d}s` }} />
              ))}
            </div>
          ) : (
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/70 hidden sm:inline">Temple Ambience</span>
          )}
        </button>
        <button
          type="button"
          data-testid="ambient-volume-open"
          onClick={() => setOpenPanel((v) => !v)}
          aria-label="Ambience volume"
          className="rounded-full text-white/50 hover:text-[#FF9933] hover:bg-white/5 px-2 py-1.5 text-[10px] uppercase tracking-widest transition"
        >
          Vol
        </button>
      </div>

      <AnimatePresence>
        {openPanel && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="glass-strong rounded-2xl p-4 w-64"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF9933]">Ambience Volume</div>
              <button
                type="button"
                onClick={() => setOpenPanel(false)}
                aria-label="Close"
                className="text-white/50 hover:text-white"
              >
                <X size={14} />
              </button>
            </div>
            <input
              data-testid="ambient-volume-slider"
              type="range"
              min="0"
              max="100"
              value={Math.round(volume * 100)}
              onChange={(e) => setVolume(Number(e.target.value) / 100)}
              className="w-full accent-[#FF9933]"
              aria-label="Ambience volume"
            />
            <div className="mt-2 text-[11px] text-white/50">
              {Math.round(volume * 100)}% · Ambience is independent from music playback.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default AmbientDock;
